import { useMemo } from 'react'

import { TokenListItem } from '~/components/component-library/TokenSelector/TokenSelectorTokenList'
import { getDisplayTokens } from '~/components/component-library/TokenSelector/getDisplayTokens'
import {
  NetworkFilterState,
  SortBy,
} from '~/components/component-library/TokenSelector/types'
import { QUERY_KEYS } from '~/constants/react-query'
import { AggregatedBalance } from '~/hooks/useAggregatedBalances'
import { useTokenWhitelist } from '~/hooks/useDashboard'
import { useFrequentlyUsedTokens } from '~/hooks/useFrequentlyUsedTokens'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { RelayCurrencySearchResultWithChainId } from '~/hooks/useRelayCurrencySearch'
import { useServerRequests } from '~/hooks/useRequests'
import { Address } from '~/utils/address'

type UseDisplayTokensParams = {
  balances: AggregatedBalance[]
  relayResults: RelayCurrencySearchResultWithChainId[]
  activeChainIds: number[]
  networkFilter: NetworkFilterState
  searchQuery: string
  tokenFilter?: (token: { address: string; chainId: number }) => boolean
  strictTokenFilter?: boolean
  showBalances: boolean
  /** Tokens to always show first, regardless of balance or frequency */
  promotedTokens?: { address: string; chainId: number }[]
  allowFrequentlyUsedFromNonActiveChains?: boolean
  sortBy: SortBy
}

type TokenMetadataSearchResponse = {
  metadata: {
    address: string
    chainId: number
    symbol: string
    decimals: number
    tokenId: string
  }
}

type TokenMetadataCandidate = {
  address: string
  chainId: number
}

function useExactAddressMetadataTokens({
  activeChainIds,
  networkFilter,
  searchQuery,
}: {
  activeChainIds: number[]
  networkFilter: NetworkFilterState
  searchQuery: string
}) {
  const { serverPostRequest } = useServerRequests()
  const metadataCandidates = useMemo<TokenMetadataCandidate[]>(() => {
    const normalizedSearchQuery = searchQuery.trim()
    if (!Address.validate(normalizedSearchQuery, 'evm')) return []

    const chainIds =
      networkFilter.type === 'all' ? activeChainIds : [networkFilter.chainId]

    return chainIds.map((chainId) => ({
      address: Address.format(normalizedSearchQuery, 'evm'),
      chainId,
    }))
  }, [activeChainIds, networkFilter, searchQuery])
  const metadataCandidatesKey = metadataCandidates
    .map((token) => `${token.chainId}:${token.address}`)
    .join(',')
  const { data: metadataResults = [] } = useQueryWrapper<
    TokenMetadataSearchResponse['metadata'][]
  >({
    queryKey: [QUERY_KEYS.TOKEN_METADATA_SEARCH, metadataCandidatesKey],
    enabled: metadataCandidates.length > 0,
    queryFn: async () => {
      const response = await serverPostRequest<TokenMetadataSearchResponse>({
        path: '/api/tokens/metadata',
        params: { tokens: metadataCandidates },
        errorMessage: '',
      })

      return response?.data.metadata ?? []
    },
    placeholderData: [],
  })

  return useMemo(
    () =>
      metadataResults.map((metadata) => ({
        address: metadata.address,
        chainId: metadata.chainId,
      })),
    [metadataResults],
  )
}

export function useDisplayTokens({
  balances,
  relayResults,
  activeChainIds,
  networkFilter,
  searchQuery,
  tokenFilter,
  strictTokenFilter,
  showBalances,
  promotedTokens,
  allowFrequentlyUsedFromNonActiveChains,
  sortBy,
}: UseDisplayTokensParams): TokenListItem[] {
  const { frequentlyUsedTokens } = useFrequentlyUsedTokens()
  const tokenWhitelist = useTokenWhitelist()
  const exactAddressMetadataTokens = useExactAddressMetadataTokens({
    activeChainIds,
    networkFilter,
    searchQuery,
  })

  return useMemo(
    () =>
      getDisplayTokens({
        balances,
        relayResults,
        activeChainIds,
        networkFilter,
        searchQuery,
        tokenFilter,
        strictTokenFilter,
        showBalances,
        frequentlyUsedTokens: frequentlyUsedTokens ?? [],
        tokenWhitelist,
        exactAddressMetadataTokens,
        promotedTokens,
        allowFrequentlyUsedFromNonActiveChains,
        sortBy,
      }),
    [
      balances,
      relayResults,
      activeChainIds,
      networkFilter,
      searchQuery,
      tokenFilter,
      strictTokenFilter,
      showBalances,
      frequentlyUsedTokens,
      tokenWhitelist,
      exactAddressMetadataTokens,
      promotedTokens,
      allowFrequentlyUsedFromNonActiveChains,
      sortBy,
    ],
  )
}
